import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "@/shadcn/lib/utils";

import { Button, type ButtonProps } from "../Button/Button";

const SIZE_CLASSES = {
  "sm": "max-w-md",
  "md": "max-w-xl",
  "lg": "max-w-2xl",
  "lg-wide": "max-w-4xl",
  "xl": "max-w-5xl",
  "xl-wide": "max-w-6xl",
} as const;

type ModalAction = {
  readonly text: string;
  readonly loading?: boolean;
  readonly onClick?: () => void;
  readonly variant?: ButtonProps["variant"];
};

export type ModalProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly subtitle?: React.ReactNode;
  readonly children: React.ReactNode;
  readonly confirm?: ModalAction;
  readonly cancel?: ModalAction;
  /**
   * When set, the confirm button becomes `type="submit" form={confirmFormId}`,
   * so it submits the form with that id even when the form is rendered inside the modal body.
   * Lets Enter-to-submit work natively without manual onClick wiring.
   */
  readonly confirmFormId?: string;
  readonly size?: keyof typeof SIZE_CLASSES;
  readonly theme?: string;
  /**
   * Render the modal at an elevated z-index so it sits clearly above another
   * open modal (and dims the modal underneath). Use when opening a confirmation
   * or follow-up dialog while a parent modal is still mounted.
   */
  readonly elevated?: boolean;
};

export const Modal = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  confirm,
  cancel,
  confirmFormId,
  size = "md",
  theme,
  elevated = false,
}: ModalProps) => {
  const hasActions = Boolean(confirm || cancel);

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 bg-neutral-800/50",
            elevated ? "z-130" : "z-120",
            "data-[state=closed]:animate-dialogOverlayOut data-[state=open]:animate-dialogOverlayIn",
          )}
        />

        <DialogPrimitive.Content
          className={cn(
            "pointer-events-none fixed inset-0 flex items-center justify-center p-4",
            elevated ? "z-131" : "z-121",
            "focus:outline-hidden",
            "[&[data-state=closed]_[data-modal-surface]]:animate-dialogSurfaceOut",
            "[&[data-state=open]_[data-modal-surface]]:animate-dialogSurfaceIn",
          )}
          {...(theme ? { "data-theme": theme } : {})}
        >
          <div
            className={cn(
              "pointer-events-auto relative grid max-h-[90vh] w-full gap-4 overflow-y-auto rounded-2xl border border-border bg-white p-6 shadow-xl",
              SIZE_CLASSES[size],
            )}
            data-modal-surface
          >
            <div className="grid gap-1 pr-10">
              <DialogPrimitive.Title>
                <span className="block type-heading-md text-foreground">{title}</span>
              </DialogPrimitive.Title>
              {subtitle && (
                <DialogPrimitive.Description asChild>
                  {typeof subtitle === "string" ? (
                    <span className="block type-body-sm text-muted">{subtitle}</span>
                  ) : (
                    <div className="type-body-sm text-muted-foreground">{subtitle}</div>
                  )}
                </DialogPrimitive.Description>
              )}
              {!subtitle && (
                <DialogPrimitive.Description className="sr-only">
                  {title}
                </DialogPrimitive.Description>
              )}
              <DialogPrimitive.Close asChild>
                <Button
                  aria-label="Close dialog"
                  className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center p-0"
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <X aria-hidden="true" className="h-5 w-5" />
                </Button>
              </DialogPrimitive.Close>
            </div>

            <div>{children}</div>

            {hasActions && (
              <div className="flex items-center justify-end gap-2 pt-1">
                {cancel && (
                  <Button
                    size="md"
                    type="button"
                    variant={cancel.variant ?? "ghost"}
                    onClick={cancel.onClick}
                  >
                    {cancel.text}
                  </Button>
                )}
                {confirm && (
                  <Button
                    form={confirmFormId}
                    loading={confirm.loading}
                    size="md"
                    type={confirmFormId ? "submit" : "button"}
                    variant={confirm.variant ?? "primary"}
                    onClick={confirm.onClick}
                  >
                    {confirm.text}
                  </Button>
                )}
              </div>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
